import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Flex } from '@fundamentals'
import { useIntercom } from 'react-use-intercom'
import { PageLayoutProps, TopBarProps } from './types'
import { useScrolling } from '@common/hooks'
import { NextRouter } from 'next/router'
import { NavigationAwareSidebar } from 'webapp/components/elements/NavigationAwareSidebar'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { useWebAuthContext } from 'webapp/providers/authentication'
import Constants from '@core/utils/constants'
import { useThemeProps, useTheme } from '@mui/material/styles'
import { intercomStorageAdapter } from 'webapp/providers/authentication/intercomToken'
import { NavBar } from '@common/Navbar/NavBar'
import { NavigationAwareBreadcrumbs } from '@elements/NavigationAwareBreadcrumbs/NavigationAwareBreadcrumbs'
import { IconButton, Tooltip } from '@common/material'
import { SideBarRefType } from '@common/material'
import { Menu as MenuIcon } from '@mui/icons-material'
import { localizedStrings } from '@core/strings'
import { ScrollContainerProvider } from '@common/ScrollContainerContext'

const TopBar: React.FC<TopBarProps & { onExpandSidebar?: () => void }> = ({
  onExpandSidebar,
  ...props
}) => {
  const { iconButtonSx } = useThemeProps({
    name: 'Sidebar',
    props: null,
  })
  return (
    <NavBar {...props}>
      <Flex alignItems='center' gap={1}>
        {onExpandSidebar && (
          <Tooltip title={localizedStrings.showMenu} placement='bottom'>
            <IconButton
              size='small'
              onClick={onExpandSidebar}
              aria-label={localizedStrings.showMenu}
              sx={iconButtonSx}
            >
              <MenuIcon sx={{ width: 16, height: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <NavigationAwareBreadcrumbs />
      </Flex>
    </NavBar>
  )
}

const PageLayout: React.FC<PageLayoutProps> = ({
  page,
  router,
  fullscreen,
  isDashboard = false,
  pageSx,
  withoutBottomPadding,
  applyContentMaxWidth = false,
  excludePadding = false,
  autoCollapseSidebarBelow,
  ...props
}) => {
  const storedIntercomToken = intercomStorageAdapter.getToken()
  const { data: profile } = useGetProfile({
    intercomToken: storedIntercomToken,
  })
  const { boot, update } = useIntercom()
  const { scrollingElementId } = useScrolling()
  const contentRef = useRef<HTMLDivElement>(null)
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null,
  )
  const handleContentRef = useCallback((element: unknown) => {
    const contentElement = element as HTMLDivElement | null
    contentRef.current = contentElement
    setScrollContainer(contentElement)
  }, [])

  const { sidebarWidth: themeSidebarWidth } = useThemeProps({
    name: 'Sidebar',
    props: null,
  })
  const sidebarRef = useRef<SideBarRefType>(null)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const theme = useTheme()
  const containerHorizontalPadding = 48

  const { isReady } = useWebAuthContext()

  const isFullscreen = (router: NextRouter): boolean => {
    return router.query?.fullScreen === 'true'
  }

  useEffect(() => {
    if (!Constants.E2E && profile) {
      boot({
        email: profile.email,
        userId: profile.id.toString(),
        name: `${profile.firstName} ${profile.lastName}`,
        phone: profile.phone,
        companies: profile.organisations?.map((o) => {
          return { companyId: o.id.toString(), name: o.name, role: o.role }
        }),
      })

      const currentIntercomToken = profile.intercomCredentials?.token

      if (
        currentIntercomToken &&
        currentIntercomToken !== storedIntercomToken
      ) {
        update({
          intercomUserJwt: currentIntercomToken,
        } as any)
        intercomStorageAdapter.setToken(currentIntercomToken)
      }
    }
  }, [boot, profile])

  const maxWidthStyle = {
    width: '100%',
    maxWidth: `${
      theme.breakpoints.values.lg -
      themeSidebarWidth -
      containerHorizontalPadding
    }px`,
    alignSelf: 'center',
  }

  if (!isReady) {
    return null
  }

  if (isFullscreen(router) || fullscreen) {
    return (
      <Flex
        ref={contentRef}
        minHeight='100vh'
        height='100vh'
        width='100%'
        bgcolor='#FAFAFA'
        flexDirection={isDashboard ? 'column' : 'row'}
        {...props}
      >
        {isDashboard && <TopBar />}
        {page}
      </Flex>
    )
  } else {
    return (
      <Flex
        minHeight='100vh'
        height='100vh'
        width='100%'
        bgcolor='#FAFAFA'
        {...props}
      >
        <NavigationAwareSidebar
          ref={sidebarRef}
          contentRef={contentRef}
          onCollapsedChange={setSidebarCollapsed}
          autoCollapseBelow={autoCollapseSidebarBelow}
        />
        <Flex
          ref={handleContentRef}
          id={scrollingElementId}
          flexDirection='column'
          position='relative'
          flex={5}
          sx={{
            pb: withoutBottomPadding ? 0 : 8.5,
            overflow: 'auto',
            ...pageSx,
          }}
        >
          <ScrollContainerProvider value={scrollContainer}>
            <TopBar
              pb={excludePadding ? 0 : 2}
              onExpandSidebar={
                sidebarCollapsed
                  ? () => sidebarRef.current?.expand()
                  : undefined
              }
            />
            <Flex
              flexDirection='column'
              flex={1}
              sx={{
                pb: withoutBottomPadding || excludePadding ? 0 : 3,
                px: excludePadding ? 0 : 3,
              }}
              {...(applyContentMaxWidth && maxWidthStyle)}
            >
              {page}
            </Flex>
          </ScrollContainerProvider>
        </Flex>
      </Flex>
    )
  }
}

export { PageLayout }
